<template>
    <div class="expire-action-manager">
        <div v-if="loading" key="loadingState" class="text-muted">
            <b-skeleton width="100%" height="2rem"></b-skeleton>
        </div>
        <div v-else key="loadedState" class="">
            <div
                v-if="!easCount"
                key="noItems"
                class="expire-action-spec-single text-uppercase text-muted text-center font-size-70 py-2"
            >  <!-- NOTE: expire-action-spec-single styles are set in ExpireActionSingle component (using that for consistency) -->
                {{ localNoAdd ? localNoAddMessage : 'No actions set' }}
            </div>
            <div v-else key="hasItems" class="d-flex flex-column gap-2">
                <ExpireActionSingle 
                    v-for="eas in expireActionSpecs"
                    :key="eas.localId"
                    :easData="eas"
                    :readOnly="isEasReadOnly(eas)"
                    :narrow="narrow"
                    @updated="handleEasUpdated"
                    @deleted="handleEasDeleted"
                    @edit-started="handleEasEditStarted"
                    @edit-stopped="handleEasEditStopped"
                />  <!-- :compItemId="compItemId"
                    :vendorCompItemId="vendorCompItemId" -->
            </div>
            <div v-if="!localNoAdd" class="mt-2">
                <!-- ONLY SHOW THIS IF WE HAVE EXPIRATION DATE! use flow action/transition?-->
                <b-button
                    v-if="currentTuIsFullUser"
                    variant="subtle-primary"
                    size="sm"
                    :disabled="hasActiveEdit || (limitOnePerTu && hasExpireActionForTu)"
                    @click="startAddAction"
                >+ Add Action for this {{ isVendorCompItem ? 'Vendor Document' : 'Document Type' }}</b-button>
                <div v-if="limitOnePerTu && hasExpireActionForTu" class="d-inline-block text-muted font-size-70 ml-2">
                    You already have a reminder set
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import { ExpireActionSpec } from './shared/compModels';
import ExpireActionSingle from './ExpireActionSingle.vue';
// import StaffDebug from '../shared/StaffDebug.vue';


export default {
    name: 'ExpireActionManager',
    components: {
        // StaffDebug,
        ExpireActionSingle,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        // you can now pass in both compItemId and vendorCompItemId, but only one should be used at a time
        // if we pass in vendorCompItemId, then we are dealing with a vendor comp item, and we will only allow adding VCI EAS, but the list will include EAS from both VCI and CompItem
        compItemId: {
            type: String,
            required: false,
        },
        vendorCompItemId: {
            type: String,
            required: false,
        },
        readOnly: {  // if true, disable all editing AND adding (will set localNoAdd to true)
            type: Boolean,
            default: false,
        },
        noAdd: { // do not use directly - use localNoAdd
            type: Boolean,
            default: false,
        },
        noAddMessage: {  // pass a message to show if noAdd is true (can be dynamic!) - do not use directly - use localNoAddMessage
            type: String,
            default: '',
        },
        narrow: {  // if true, will use a narrower layout for the EAS - set true if you want to use this in a sidebar or similar
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            easById: {},  // store of expireAction specs from the API, keyed by LOCAL ID
            // ALL OPTIONS FOR EAS - all arrays have { label, value } (maybe other stuff too)
            easOptions: {},  // store of action_types / schedule_types / event_types / recipient types specs from the API, keyed by LOCAL ID
            action_types: [],  // array of action types for the EAS form -- REMINDER_TASK, SEND_EMAIL, etc.
            event_types: [],  // array of event types for the EAS form -- DUE_DATE, EXPIRE_DATE, etc.
            recipient_types: [],  // array of recipient types for the EAS form -- VENDOR, INTERNAL, SPECIFIC_TU, COMP_ITEM_ROLE, etc.
            recipient_object_roles: [],  // { label, value } where value is the ObjectRole ID, label is the ObjectRole name -- will be CompItem roles (maybe expand later)
            schedule_types: [],  // array of schedule types for the EAS form -- BEFORE_SINGLE, AFTER_SINGLE, AFTER_RECUR, etc.
            activeEditLocalIds: [],  // array of local IDs of EAS that are currently being edited -- set this from handlers of ExpireActionSingle events
            // CONFIGS (the EasActionType configs) that are available for this comp item or vendor comp item
            loadingConfigs: false,
            configs: [],  // array of config objects from the API 
            // CHANGE BELOW LATER
            limitOnePerTu: false,  // if true, only allow one expire action per tenant user (currentTuId) - this is a temporary setting for now
        }
    },
    computed: {
        isCompItem() {
            return !!this.compItemId && !this.vendorCompItemId;
        },
        isVendorCompItem() {
            return !!this.vendorCompItemId;
        },
        expireActionSpecs() {
            return Object.values(this.easById); // sorting???
        },
        easCount() {
            return this.expireActionSpecs.length;
        },
        hasActiveEdit() {
            return this.activeEditLocalIds.length > 0;
        },
        currentTuId() {
            return this.$currentUserInfo?.tenant_user_id || '';
        },
        currentTuIsFullUser() {
            return !!this.$currentUserInfo?.is_paid_seat;
        },
        currentTuIsTenantAdmin() {
            return !!this.$currentUserInfo?.is_tenant_admin;
        },
        hasExpireActionForTu() {  // we should probably allow this in the future - might want reminders at different times
            return this.expireActionSpecs.some(eas => eas.recipient_tu_id === this.currentTuId);
        },
        localNoAdd() {
            return this.noAdd || this.readOnly;
        },
        localNoAddMessage() {
            return this.readOnly ? 'No actions set' : this.noAddMessage;
        },
    },
    created() {
        // if (this.compItemId && this.vendorCompItemId) {
        //     throw new Error('ExpireActionManager: must pass in only one of compItemId or vendorCompItemId - not both');
        // }  // this is OK now
    },
    mounted() {
        this.getExistingEAS();
    },
    methods: {
        async getExistingEAS() {
            this.loading = true;
            let resp;
            try {
                if (this.isCompItem) {
                    resp = await suppliosApi.compItemStd.get_expire_action_specs(this.compItemId);
                } else if (this.isVendorCompItem) {
                    resp = await suppliosApi.vendorCompItemStd.get_expire_action_specs(this.vendorCompItemId, true);
                }
                // console.log(`ExpireActionManager: getExistingEAS response`, resp);
                const expireActionSpecs = Array.isArray(resp.data) ? resp.data.map(eas => new ExpireActionSpec(eas)) : [];
                // console.log('ExpireActionManager: parsed expireActionSpecs', expireActionSpecs);
                // TODO - get metadata from API also to know if we can add more, options available, etc.
                this.easById = Object.fromEntries(expireActionSpecs.map(easInst => [easInst.localId, easInst]));
                // console.log('ExpireActionManager: easById', this.easById);
            } catch (e) {
                this.apiErrorHandler(e);
            }
            this.loading = false;
        },
        setInLocalStore(eas) {
            // set the EAS in the easById store - use to create new, or overwrite existing
            this.$set(this.easById, eas.localId, eas);
        },
        deleteFromLocalStore(eas) {
            // remove the EAS from the easById store
            this.$delete(this.easById, eas.localId);
        },
        isEasReadOnly(easObj) {
            // check if the EAS is read-only based on the current scope we are in
            // if we are in VCI scope here, then we only allow editing EAS for that VCI
            // if we are in CompItem scope, then we should not even have VCI EAS...
            if (this.readOnly) {
                return true;  // if readOnly prop is set, then all EAS are read-only
            }
            if (this.isVendorCompItem) {
                // if vendorCompItemId is set, then only allow editing EAS for that VCI
                return easObj.vendor_comp_item_id !== this.vendorCompItemId;  // IS read-only if it does not match the vendorCompItemId
            } else {
                return easObj.comp_item_id !== this.compItemId;  // IS read-only if it does not match the compItemId (should not really happen...)
            }
        },
        // TODO - we probably SHOULD add this here, so we select the config here before making a new instance and creating new EAS Single component instance
        //      - but then we also have to pass down all the available options for the selected config to the child component
        //      - not difficult, but let's not mess things up right now....
        // async fetchAvailableEasConfigs() {
        //     // fetch the possible EAS configs from the API - action types, event types, recipient types, schedule types, etc.
        //     // we need these to show the options for adding a new EAS -- call it anytime we add a new one (in case there is something dynamic based on existing ones)
        //     // this is also called from ExpireActionSingle if doing updates
        //     this.loadingConfigs = true;
        //     let resp;
        //     try {
        //         // NOTE: if you pass both of these, it will only use the more specific one (VCI over CI)
        //         resp = await suppliosApi.compEas.get_eas_options(this.compItemId, this.vendorCompItemId);
        //     } catch (e) {
        //         this.apiErrorHandler(e);
        //     }
        //     if (resp?.data) {
        //         console.log('expire action options', resp.data);
        //         this.configs = resp.data.configs || [];  // these are the presets, if any
        //     }
        //     this.loadingConfigs = false;
        // },
        async startAddAction() {
            // create empty EAS and add to the store, child will handle the rest
            const newEAS = new ExpireActionSpec({ 
                comp_item_id: (this.compItemId || null),  // should always have this now, even if vendorCompItemId is set
                vendor_comp_item_id: (this.vendorCompItemId || null),
                duration_num_periods: 1,
                duration_period: 'weeks',
            });
            this.setInLocalStore(newEAS);
            // this.startEditAction(newEAS);
        },
        emitUpdated() {
            // tell parent something changed so it can refresh
            this.$emit('updated');
        },
        // handlers for events emitted by ExpireActionSingle
        handleEasUpdated(eas) {
            // called when an EAS is updated (saved)
            console.log('handleEasUpdated', eas);
            this.setInLocalStore(eas);
            this.emitUpdated();
        },
        handleEasDeleted(eas) {
            // called when an EAS is deleted
            console.log('handleEasDeleted', eas);
            // TODO - check if it was persisted, if not, then don't call emitUpdated
            const wasPersisted = eas.isPersisted();  // this is set in ExpireActionSingle
            this.deleteFromLocalStore(eas);
            if (wasPersisted) {
                // if it was persisted, we need to tell the parent to refresh
                console.log('ExpireActionManager: EAS was persisted, emitting updated');
                this.emitUpdated();
            } else {
                console.log('ExpireActionManager: EAS was NOT persisted, not emitting updated');
            }
            // if it was not persisted, we don't need to tell the parent to refresh
        },
        // NOTE: we may no longer need to track active edits... FYI.
        handleEasEditStarted(eas) {
            console.log('handleEasEditStarted', eas);
            if (!this.activeEditLocalIds.includes(eas.localId)) {
                this.activeEditLocalIds.push(eas.localId);
            }
        },
        handleEasEditStopped(eas) {
            console.log('handleEasEditStopped', eas);
            this.activeEditLocalIds = this.activeEditLocalIds.filter(i => (i !== eas.localId));
        },
    },
}
</script>

<style lang="scss">

// .expire-action-manager {  // moved to single component
//     .expire-action-spec {
//         border: 1px solid var(--gray-300);
//         background: #fff; // var(--gray-50);
//         border-radius: 0.25rem;
//         padding: 4px 8px;
//         margin-bottom: 6px;
//         &.locked {
//             opacity: 0.7;
//         }
//         &.editing {
//             opacity: 1;
//             background-color: #fff;
//         }
//         &.pending-delete {
//             background-color: var(--danger-50);
//         }
//     }
// }

</style>